/**
 * The gaps of a studio draft: what it is missing for its first day, found by
 * fixed rules (never a model), each with a question, chips and, where one
 * exists, a ready fix made of Phase 2 ops (step 3.2 of
 * docs/plans/onboarding-harness/phase-03.md).
 *
 * Words are keys (`setup.studio.gap.*`) with named vars; a gap's text never
 * holds an app's name, because a rule only ever reads the draft's own names.
 * A var whose value is itself a `setup.studio.gap.` key (`what`) is a word to
 * look up before it fills the sentence: `gapVarWords`.
 *
 * A chip's ops are a fix only where they commit: `POST …/ops` judges them
 * with every draft-level rule (`studioCommit`, `studioProblems`), not only
 * the apply. So a rule raises a check only on a part its fix is valid for (no
 * money on a list), and where the fix would not fit (a ceiling reached) its
 * chip carries no ops: words for OS, with no Decide for me.
 * `setup-gaps-guard.ts` holds every rule to it in the tests.
 *
 * Pure: the phone bundles it.
 */
import { LINES_MAX_FIELDS } from './api';
import { areaOfPlace, nameSuggestionsOf, sameWorkspaceName } from './setup-names';
import type { SetupDraftField, SetupDraftFormulaNode } from './compose-author';
import { editChangesOf, editNotYetKey, type EditBase } from './edit-changes';
import type { AppKey } from './i18n/en';
import { walkSetup, type WalkCheckId } from './setup-walk';
import {
  mintStudioKey,
  STUDIO_LIMITS,
  studioPartExists,
  studioPath,
  studioCommit,
  studioProblems,
  studioRefOf,
  type StudioProblem,
  type StudioDraft,
  type StudioFieldInput,
  type StudioOp,
  type StudioPartRef,
  type StudioRecord,
  type CriticCheck,
  accountRowName,
  accountSeedRow,
  accountsListOf,
  GAP_SETTLED,
  isMoneyKitForm,
  isMoneyRecord,
  kindGuess,
  modeAccountOf,
  MONEY_KINDS,
  MONEY_NONE_REASON,
  moneyPlanOf,
  moneyPlanOps,
  partyListsOf,
  postingOf,
  saysHowPaid,
  type MoneyKind,
} from './setup-draft';

export const GAP_RULE_IDS = [
  'no_name',
  'name_taken',
  'no_record',
  'dangling_reference',
  'credit_without_dues',
  'sale_without_payment',
  'qty_without_rate',
  'stock_without_movement',
  'watch_unanswered',
  'role_reach',
  'source_conflict',
  'source_unplaced',
  'rows_unsure',
  'orphan_list',
  'record_without_card',
  'link_fixed_fill',
  'link_sign_type',
  'link_fill_missing',
  'same_job',
  'draft_problem',
  'edit_not_yet',
  'documents_ready',
  'money_kind',
  'for_home',
  'account_type',
  'figure_label_taken',
  'setup_walk',
  'who_records',
] as const;
export type GapRuleId = (typeof GAP_RULE_IDS)[number];

export type GapSeverity = 'blocker' | 'should' | 'could';

export type GapChip = {
  readonly labelKey?: AppKey;
  /** Literal words: a name the draft or a place holds, or a critic's chip. */
  readonly label?: string;
  readonly vars?: Readonly<Record<string, string>>;
  readonly ops: readonly StudioOp[];
  /** Picking it leaves the gap: 3.3 turns it into the `dismiss_gap` op. */
  readonly dismiss?: true;
};

export type SetupGap = {
  /** `<rule>:<ref key or "workspace">`. */
  readonly id: string;
  readonly rule: GapRuleId | 'critic';
  readonly severity: GapSeverity;
  readonly on: StudioPartRef | null;
  readonly titleKey?: AppKey;
  readonly questionKey?: AppKey;
  readonly vars: Readonly<Record<string, string>>;
  /** Critic only: literal text. */
  readonly title?: string;
  /** Critic, source conflicts and a link's draft problems only: literal text. */
  readonly question?: string;
  readonly why?: string;
  readonly chips: readonly GapChip[];
  /** The chip Decide for me takes; its ops are the gap's ready fix. */
  readonly defaultChip: number | null;
  /** Sources that disagree only (9.10): what about, and each side's source and value, in the chips' order. */
  readonly conflict?: { readonly id: string; readonly about: string; readonly sides: readonly ConflictSide[] };
  /** Sources that disagree only: the person chose Decide later; the gap stays a thing to check. */
  readonly later?: true;
};

/** One side of a disagreement: which source (or `said`), its name, and the value it gives. */
export type ConflictSide = { readonly sourceId: string; readonly name: string; readonly value: string };

/** What the rules read beside the draft: the session's sources and where they disagree are the server's (`gapFactsFor`, step 4.13). */
export type GapFacts = {
  /** The names of the workspaces the person already holds. */
  heldNames: string[];
  /** The person's lines, lower-cased, joined. */
  said: string;
  place: { name: string; area: string } | null;
  sources: { id: string; state: 'read' | 'partly' | 'failed' | 'reading' | 'left'; placed: boolean; name: string }[];
  conflicts: { id: string; a: string; b: string; question: string; opsA: StudioOp[]; opsB: StudioOp[]; on: StudioPartRef; about?: string; sides?: ConflictSide[] }[];
  /**
   * A file's sheet whose rows setup could not place on its own (step 2.2 of
   * docs/plans/setup-file-data.md): the column matcher and the model
   * disagree, or neither is sure. Each option is a record or list the rows
   * could go to, with the ops that plan them there. Absent: none.
   */
  rows?: {
    id: string;
    source: string;
    sheet: string;
    name: string;
    options: { record: string; name: string; ops: StudioOp[] }[];
    /** The sheet's rows: what Leave out says it leaves out. */
    rows?: number;
    /**
     * The kinds of rows the sheet mixes (W8 of docs/plans/workbook-map.md):
     * its one option puts each kind into its own record, `name` naming them.
     */
    kinds?: number;
    /** The sheets the file holds, when more than one: the question names the sheet, so two are told apart. */
    sheets?: number;
  }[];
  /**
   * The business details setup holds as candidates (D14 of
   * docs/plans/report-templates.md), by key: `gstin` says invoices may follow
   * (`documents_ready`). Absent: none.
   */
  detailKeys?: readonly string[];
  /**
   * An edit session's base (9.16): the workspace as it stood when Edit opened.
   * Set, the draft is an edit of a built workspace, so only the rules that
   * judge what the person changes run, and each difference Build again leaves
   * as it is raises an `edit_not_yet` gap on its part.
   */
  edit?: EditBase;
};

export const NO_GAP_FACTS: GapFacts = { heldNames: [], said: '', place: null, sources: [], conflicts: [] };

/** Whether two workspace names are the same name: without case or the spaces round them (`setup-names.ts`, 9.11). */
export { sameWorkspaceName };

/**
 * The workspace's name checks: never asked as a card in the thread (Devendra,
 * 16 Oct). They stay in the checks and the turn's closing line names them.
 */
export const NAME_GAP_RULES: readonly GapRuleId[] = ['no_name', 'name_taken'];

/** Whether a gap id is one of the name checks (`no_name:workspace`, `name_taken:workspace`). */
export const isNameGapId = (id: string | null | undefined): boolean =>
  typeof id === 'string' && NAME_GAP_RULES.some((rule) => id === `${rule}:workspace` || id.startsWith(`${rule}:`));

/** The chip every `should` and `could` gap ends with. */
export const NOT_NEEDED_CHIP: GapChip = { labelKey: 'setup.studio.push.not_needed', ops: [], dismiss: true };

/** A critic's chip worded Not needed, as checks written before `keeps` say it: the gap's own Not needed. */
const CRITIC_NOT_NEEDED = /^\s*(not needed|no need)\s*$/i;

/**
 * A critic's chip as its gap offers it: one that keeps the draft as it is
 * (`keeps`, or worded Not needed) leaves the check, as Not needed does, with
 * no turn to say so; any other is words for OS.
 */
const criticChip = (chip: CriticCheck['chips'][number]): GapChip =>
  CRITIC_NOT_NEEDED.test(chip.label) ? NOT_NEEDED_CHIP : chip.keeps === true ? { label: chip.label, ops: [], dismiss: true } : { label: chip.label, ops: [] };

/** The vars of a gap or chip with every `setup.studio.gap.` key among their values put in words by `word`. */
export const gapVarWords = (vars: Readonly<Record<string, string>> | undefined, word: (key: AppKey) => string): Record<string, string> =>
  Object.fromEntries(Object.entries(vars ?? {}).map(([name, value]) => [name, value.startsWith('setup.studio.gap.') ? word(value as AppKey) : value]));

/* ------------------------------------------------------------------ helpers */

// Whole words only: `owe` must not find "flowers", nor `rate` "Corporate", nor `mode` "Model".
const CREDIT = /\b(credit|udhaar|udhar|baaki|baki|khata|pay later)\b/i;
const PAID = /\b(paid|received|jama)\b/i;
const RATE = /\b(rates?|prices?|bhav)\b/i;
const STOCK = /\b(stocks?|inventory|maal|in hand)\b/i;
const OWES = /\b(owe|owes|owed|owing|dues?|baaki|udhaar|credit)\b/i;

type FieldAt = { field: SetupDraftField; within: string[] };

/** Every field of a record, line fields too, with the chain of lines keys above each. */
const allFields = (fields: readonly SetupDraftField[], within: string[] = []): FieldAt[] =>
  fields.flatMap((field) => [
    { field, within },
    ...(field.type === 'lines' && field.lines !== undefined ? allFields(field.lines.fields, [...within, field.key]) : []),
  ]);

const isList = (record: StudioRecord): boolean => record.kind === 'directory';
/** An entry record: the only kind that holds typed money (`DIRECTORY_HOLDS_MONEY`); a list only works money out from entries (W11 of docs/plans/workbook-map.md). */
const isEntries = (record: StudioRecord): boolean => record.kind === 'record';

/** Whether `adding` more fields fit beside `fields`: 8 on a line (`LINES_FIELDS_MAX`), the record's ceiling at the top. */
const fieldsFit = (fields: readonly unknown[], within: readonly string[], adding: number): boolean =>
  fields.length + adding <= (within.length === 0 ? STUDIO_LIMITS.fields : LINES_MAX_FIELDS);
/** Whether one more record or list fits in the draft. */
const recordFits = (draft: StudioDraft): boolean => draft.records.length < STUDIO_LIMITS.records;
/** Whether one more card fits on a record (`tilesPerRecord`). */
const tileFits = (record: StudioRecord): boolean => (record.tiles?.length ?? 0) < STUDIO_LIMITS.tilesPerRecord;
/** Whether one more not-yet item fits (`notYet`). */
const notYetFits = (draft: StudioDraft): boolean => draft.notYet.length < STUDIO_LIMITS.notYet;
/** A fix's ops where they fit, else none: the chip becomes words for OS. */
const fitting = (fits: boolean, ops: StudioOp[]): StudioOp[] => (fits ? ops : []);
const isMoney = (field: SetupDraftField): boolean => field.type === 'money' || (field.type === 'formula' && field.formula?.result === 'money');
const refersTo = (field: SetupDraftField, list: string): boolean =>
  (field.type === 'reference' || field.type === 'multi_select') && field.targetRecordKey === list;

/** A list of people or parties: who buys, owes or pays. */
const PEOPLE = /\b(customers?|clients?|parties|party|buyers?|accounts?|members?|patients?|farmers?|tenants?|students?|debtors?|guests?|people|grahak)\b/i;
/** A list of things or places, never who owes: what is sold, where, of what kind. */
const THINGS = /\b(items?|products?|goods|materials?|menu|dish(es)?|medicines?|stocks?|sku|maal|articles?|outlets?|branch(es)?|shops?|stores?|locations?|places?|categor(y|ies)|units?|modes?)\b/i;

const listWords = (draft: StudioDraft, field: SetupDraftField): string => `${field.label} ${field.key.replace(/_/g, ' ')} ${draft.records.find((one) => one.key === field.targetRecordKey)?.name ?? ''}`;

/**
 * The pick of a record's people or parties: a pick from a list of people,
 * else its headline's pick, else its first top-level pick, never of what is
 * sold or where (R12 of docs/plans/qa-fixes-2.md: in run 2 a sale's Item was
 * taken for who owes, and Payments received picked an Item).
 */
const partyRefOf = (draft: StudioDraft, record: StudioRecord): SetupDraftField | null => {
  const lists = new Set(draft.records.filter(isList).map((one) => one.key));
  const picks = record.fields.filter((field) => field.type === 'reference' && field.targetRecordKey !== undefined && lists.has(field.targetRecordKey));
  const people = picks.find((field) => PEOPLE.test(listWords(draft, field)));
  if (people !== undefined) return people;
  const parties = picks.filter((field) => !THINGS.test(listWords(draft, field)));
  return parties.find((field) => field.key === record.headlineFieldKey) ?? parties[0] ?? null;
};

/** A list of people the draft holds, by its name: Customers, Parties, Clients. */
const peopleListOf = (draft: StudioDraft): StudioRecord | null => draft.records.find((one) => isList(one) && PEOPLE.test(`${one.name} ${one.key.replace(/_/g, ' ')}`)) ?? null;

/** Whether a formula tree anywhere reads `fieldKey`. */
const formulaReads = (node: unknown, fieldKey: string): boolean => {
  if (Array.isArray(node)) return node.some((one) => formulaReads(one, fieldKey));
  if (typeof node !== 'object' || node === null) return false;
  const held = node as Record<string, unknown>;
  if ((held.kind === 'field' && held.fieldKey === fieldKey) || held.of === fieldKey) return true;
  return Object.values(held).some((value) => formulaReads(value, fieldKey));
};

const fieldKeysOf = (fields: readonly SetupDraftField[]): Set<string> => new Set(fields.map((field) => field.key));
const recordKeys = (draft: StudioDraft): Set<string> => new Set(draft.records.map((one) => one.key));
const keyId = (record: string, within: readonly string[] = [], field?: string): string => [record, ...within, ...(field === undefined ? [] : [field])].join('.');

/** The kind of business, for a not-yet item's `archetype`: what the draft says it does, cut to 60. */
const archetypeOf = (draft: StudioDraft): string => {
  const does = (draft.meta.does ?? '').trim();
  return (does === '' ? 'small business' : does).slice(0, 60).trim();
};

/** Where a part sits in the draft, for the sort: the workspace, then records in order, then the rest. */
const orderOf = (draft: StudioDraft, on: StudioPartRef | null): number => {
  if (on === null || on.kind === 'workspace') return 0;
  const recordAt = (key: string): number => draft.records.findIndex((one) => one.key === key);
  switch (on.kind) {
    case 'record':
      return 1 + recordAt(on.record) * 1000;
    case 'field': {
      const record = draft.records.find((one) => one.key === on.record);
      const at = record === undefined ? 0 : allFields(record.fields).findIndex((one) => one.field.key === on.field && keyId('', one.within) === keyId('', on.within ?? []));
      return 1 + recordAt(on.record) * 1000 + 1 + Math.max(at, 0);
    }
    case 'card':
      return 1 + recordAt(on.record) * 1000 + 999;
    case 'role':
      return 1_000_000 + draft.roles.findIndex((one) => one.key === on.key);
    case 'board':
      return 2_000_000 + draft.boardCards.findIndex((one) => one.key === on.key);
    case 'note':
      return 3_000_000 + draft.notes.findIndex((one) => one.key === on.key);
    case 'source':
      return 4_000_000 + draft.sources.findIndex((one) => one.id === on.id);
    default:
      return 5_000_000;
  }
};

const SEVERITY_ORDER: Record<GapSeverity, number> = { blocker: 0, should: 1, could: 2 };

/* -------------------------------------------------------------------- rules */

type Raise = (gap: SetupGap) => void;
type Rule = (draft: StudioDraft, facts: GapFacts, raise: Raise) => void;

const WORKSPACE: StudioPartRef = { kind: 'workspace' };

const noName: Rule = (draft, facts, raise) => {
  if (draft.meta.name.trim() !== '') return;
  raise({
    id: 'no_name:workspace',
    rule: 'no_name',
    severity: 'blocker',
    on: WORKSPACE,
    questionKey: 'setup.studio.gap.name_q',
    vars: {},
    chips: [
      ...(facts.place === null || facts.place.name.trim() === '' ? [] : [{ label: facts.place.name.trim(), ops: [{ op: 'set_workspace' as const, name: facts.place.name.trim() }] }]),
      { labelKey: 'setup.studio.q.other', ops: [] },
    ],
    defaultChip: null,
  });
};

/** The name is one the person already holds (9.11): the names offered are `nameSuggestionsOf`'s, the same in the canvas head and in OS's question. */
const nameTaken: Rule = (draft, facts, raise) => {
  const name = draft.meta.name.trim();
  if (name === '' || !facts.heldNames.some((held) => sameWorkspaceName(name, held))) return;
  const area = areaOfPlace(draft.meta.place) ?? (facts.place?.area.trim() || null);
  const others = nameSuggestionsOf(name, { place: area, held: facts.heldNames });
  raise({
    id: 'name_taken:workspace',
    rule: 'name_taken',
    severity: 'blocker',
    on: WORKSPACE,
    questionKey: 'setup.studio.gap.name_taken_q',
    vars: {},
    chips: [
      ...others.map((other) => ({ label: other, ops: [{ op: 'set_workspace' as const, name: other }] })),
      { labelKey: 'setup.studio.gap.other_name', ops: [] },
    ],
    defaultChip: null,
  });
};

const noRecord: Rule = (draft, _facts, raise) => {
  if (draft.records.some((one) => one.kind === 'record')) return;
  raise({ id: 'no_record:workspace', rule: 'no_record', severity: 'blocker', on: WORKSPACE, questionKey: 'setup.studio.gap.no_record_q', vars: {}, chips: [], defaultChip: null });
};

const danglingReference: Rule = (draft, _facts, raise) => {
  // A pick of a list's row, or of another record's entry (a payment's bill, a room bill's booking): either is there to pick.
  const targets = new Set(draft.records.filter((one) => isList(one) || isEntries(one)).map((one) => one.key));
  for (const record of draft.records) {
    for (const { field, within } of allFields(record.fields)) {
      if (field.type !== 'reference' || (field.targetRecordKey !== undefined && targets.has(field.targetRecordKey))) continue;
      const taken = recordKeys(draft);
      const wanted = field.targetRecordKey;
      const key = wanted !== undefined && !taken.has(wanted) ? wanted : mintStudioKey(field.label, taken, 'r');
      const ops: StudioOp[] = [
        {
          op: 'add_record',
          key,
          name: field.label,
          kind: 'directory',
          direction: 'none',
          fields: [{ key: 'name', label: 'Name', type: 'text', required: true }],
          headlineFieldKey: 'name',
        },
        ...(key === wanted ? [] : [{ op: 'update_field' as const, record: record.key, ...(within.length === 0 ? {} : { within }), field: field.key, set: { targetRecordKey: key } }]),
      ];
      raise({
        id: `dangling_reference:${keyId(record.key, within, field.key)}`,
        rule: 'dangling_reference',
        severity: 'blocker',
        on: { kind: 'field', record: record.key, ...(within.length === 0 ? {} : { within }), field: field.key },
        questionKey: 'setup.studio.gap.dangling_q',
        vars: { record: record.name, field: field.label, list: field.label },
        // With no room for one more list, OS has to make room first: no chip.
        chips: recordFits(draft) ? [{ labelKey: 'setup.studio.gap.add_list', vars: { list: field.label }, ops }] : [],
        defaultChip: recordFits(draft) ? 0 : null,
      });
    }
  }
};

/** What a sale owes when it is on credit: an owed amount, a balance, what is due or left. */
const OWED_FIELD = /\b(owed?|owing|dues?|balance|outstanding|udhaar|udhar|baaki|baki|credit\s+amount|left\s+to\s+pay|unpaid)\b/i;

/**
 * Credit given with nothing that records who paid back (R12 of
 * docs/plans/qa-fixes-2.md). The fix is whole when asked: a record of
 * payments received with the customer each is from (never an item), and on
 * the sale its customer when it has none, an owed amount, and a card of what
 * each customer owes. A part the draft already holds, by its name in any
 * case, is used and never added twice (R6).
 */
/** Whether ops commit on the draft as `POST …/ops` commits them. */
const commits = (draft: StudioDraft, ops: readonly StudioOp[]): boolean =>
  studioCommit({ head: draft, headVersion: 1, headProblems: studioProblems(draft), baseVersion: 1, touchedSince: [], ops: [...ops] }).kind === 'ok';

const creditWithoutDues: Rule = (draft, facts, raise) => {
  for (const record of draft.records) {
    if (record.kind !== 'record' || record.direction !== 'in') continue;
    const fields = allFields(record.fields).map((one) => one.field);
    const creditChoice = record.fields.find((field) => field.type === 'select' && (field.options ?? []).some((option) => CREDIT.test(option.label)));
    const credit = CREDIT.test(facts.said) || fields.some((field) => field.type === 'select' && (field.options ?? []).some((option) => CREDIT.test(option.label)));
    if (!credit) continue;
    const party = partyRefOf(draft, record);
    // Who owes is a pick from a list of people: the sale's own, else a list the draft holds, else Customers, added.
    const list = party?.targetRecordKey ?? peopleListOf(draft)?.key ?? null;
    const paidBeside = fields.some((field) => isMoney(field) && field.type !== 'formula' && PAID.test(field.label)) && fields.some((field) => field.type === 'formula');
    const otherKeeps =
      list !== null &&
      draft.records.some(
        (other) => other.key !== record.key && (other.direction === 'in' || other.direction === 'both') && allFields(other.fields).some(({ field }) => refersTo(field, list)),
      );
    // A money rule that names a party keeps what each customer owes (step 9.5 of docs/plans/ledger.md: once a link into this record did).
    const ruleNamesParty = record.posting?.roles?.party !== undefined;
    if (paidBeside || otherKeeps || ruleNamesParty) continue;

    const taken = recordKeys(draft);
    const ops: StudioOp[] = [];
    let addsRecords = 0;
    let listKey = list;
    if (listKey === null) {
      listKey = mintStudioKey('Customers', taken, 'r');
      taken.add(listKey);
      addsRecords += 1;
      ops.push({
        op: 'add_record',
        key: listKey,
        name: 'Customers',
        kind: 'directory',
        direction: 'none',
        entryNoun: 'customer',
        fields: [{ key: 'name', label: 'Name', type: 'text', required: true }],
        headlineFieldKey: 'name',
      });
    }
    const partyLabel = party?.label ?? 'Customer';
    // The sale's customer, when it picks none: credit is given to someone.
    const saleKeys = fieldKeysOf(record.fields);
    let partyKey = party?.key ?? null;
    if (partyKey === null) {
      partyKey = mintStudioKey(partyLabel, saleKeys, 'f');
      saleKeys.add(partyKey);
      ops.push({ op: 'add_field', record: record.key, field: { key: partyKey, label: partyLabel, type: 'reference', targetRecordKey: listKey } });
    }
    // Payments received, by its name in any case: one the draft holds is used, with the customer added when it picks none.
    const heldPayments = draft.records.find((one) => one.kind === 'record' && one.name.trim().toLowerCase() === 'payments received');
    if (heldPayments === undefined) {
      addsRecords += 1;
      ops.push({
        op: 'add_record',
        key: mintStudioKey('Payments received', taken, 'r'),
        name: 'Payments received',
        kind: 'record',
        direction: 'in',
        entryNoun: 'payment',
        fields: [
          { key: 'date', label: 'Date', type: 'date', required: true },
          { key: 'party', label: partyLabel, type: 'reference', targetRecordKey: listKey, required: true },
          { key: 'amount', label: 'Amount', type: 'money', required: true },
          {
            key: 'paid_by',
            label: 'Paid by',
            type: 'select',
            options: [
              { key: 'cash', label: 'Cash' },
              { key: 'upi', label: 'UPI' },
              { key: 'card', label: 'Card' },
            ],
          },
        ],
        headlineFieldKey: 'party',
        dateFieldKey: 'date',
        measureFieldKey: 'amount',
      });
    } else if (!heldPayments.fields.some((field) => refersTo(field, listKey))) {
      const key = mintStudioKey(partyLabel, fieldKeysOf(heldPayments.fields), 'f');
      ops.push({ op: 'add_field', record: heldPayments.key, field: { key, label: partyLabel, type: 'reference', targetRecordKey: listKey } });
    }
    // What each sale on credit owes, and the card of what each customer owes: on a sale whose payment offers credit, beside its money.
    const measure = record.fields.find((field) => field.key === record.measureFieldKey && isMoney(field)) ?? record.fields.find((field) => isMoney(field));
    const creditOption = creditChoice?.options?.find((option) => CREDIT.test(option.label));
    const owedHeld = record.fields.find((field) => isMoney(field) && OWED_FIELD.test(field.label));
    let owedKey = owedHeld?.key ?? null;
    const core = ops.length;
    if (owedKey === null && creditChoice !== undefined && creditOption !== undefined && measure !== undefined) {
      owedKey = mintStudioKey('Owed', saleKeys, 'f');
      saleKeys.add(owedKey);
      const owed: SetupDraftFormulaNode = {
        kind: 'if',
        left: { kind: 'field', fieldKey: creditChoice.key },
        op: 'eq',
        right: { kind: 'const', value: creditOption.key },
        // A worked-out total is read by its own sum: one formula reads only what is typed in.
        then: measure.type === 'formula' && measure.formula !== undefined ? measure.formula.expr : { kind: 'field', fieldKey: measure.key },
        otherwise: { kind: 'const', value: 0 },
      };
      ops.push({ op: 'add_field', record: record.key, field: { key: owedKey, label: 'Owed', type: 'formula', formula: { result: 'money', expr: owed, onMissing: 'blank' } } });
    }
    const owesCard = (record.tiles ?? []).some(
      (tile) => tile.groupByFieldKey === partyKey && (tile.fieldKey === owedKey || (creditChoice !== undefined && (tile.where ?? []).some((one) => one.fieldKey === creditChoice.key))),
    );
    if (owedKey !== null && !owesCard && tileFits(record)) {
      ops.push({ op: 'add_card', record: record.key, card: { label: 'What each customer owes', card: 'insight.breakdown', agg: 'sum', fieldKey: owedKey, groupByFieldKey: partyKey, scope: 'all' } });
    }
    // The owed amount and its card only where they commit as the server commits them; the payments record stands without them.
    if (ops.length > core && !commits(draft, ops)) ops.splice(core);
    const fits = draft.records.length + addsRecords <= STUDIO_LIMITS.records && fieldsFit(record.fields, [], saleKeys.size - record.fields.length);

    const signKey = mintStudioKey('Gave or got', fieldKeysOf(record.fields), 'f');
    const both: StudioOp[] = [
      {
        op: 'add_field',
        record: record.key,
        field: {
          key: signKey,
          label: 'Gave or got',
          type: 'select',
          options: [
            { key: 'gave', label: 'Gave', sign: 1 },
            { key: 'got', label: 'Got', sign: -1 },
          ],
        },
      },
      { op: 'update_record', record: record.key, direction: 'both', signFieldKey: signKey },
    ];
    raise({
      id: `credit_without_dues:${record.key}`,
      rule: 'credit_without_dues',
      severity: 'should',
      on: { kind: 'record', record: record.key },
      titleKey: 'setup.studio.gap.credit_title',
      questionKey: 'setup.studio.gap.credit_q',
      vars: { record: record.name },
      chips: [
        { labelKey: 'setup.studio.gap.credit_fix', ops: fitting(fits, ops) },
        { labelKey: 'setup.studio.gap.credit_both', vars: { record: record.name }, ops: fitting(fieldsFit(record.fields, [], 1), both) },
        NOT_NEEDED_CHIP,
      ],
      defaultChip: fits ? 0 : null,
    });
  }
};

const saleWithoutPayment: Rule = (draft, _facts, raise) => {
  for (const record of draft.records) {
    if (record.kind !== 'record' || record.direction !== 'in') continue;
    const fields = allFields(record.fields).map((one) => one.field);
    if (!fields.some(isMoney)) continue;
    // Asked only where the record says nothing of how it was paid: a mode, a list of modes, or the Accounts list's
    // "Received in". Where the country is known the money pass has given it Paid by already (`paymentModeOps`).
    if (saysHowPaid(draft, record)) continue;
    const field: StudioFieldInput = {
      key: mintStudioKey('Paid by', fieldKeysOf(record.fields), 'f'),
      label: 'Paid by',
      type: 'select',
      options: [
        { key: 'cash', label: 'Cash' },
        { key: 'upi', label: 'UPI' },
        { key: 'card', label: 'Card' },
      ],
    };
    raise({
      id: `sale_without_payment:${record.key}`,
      rule: 'sale_without_payment',
      severity: 'should',
      on: { kind: 'record', record: record.key },
      questionKey: 'setup.studio.gap.payment_q',
      vars: { record: record.name },
      chips: [{ labelKey: 'setup.studio.gap.payment_fix', ops: fitting(fieldsFit(record.fields, [], 1), [{ op: 'add_field', record: record.key, field }]) }, NOT_NEEDED_CHIP],
      defaultChip: fieldsFit(record.fields, [], 1) ? 0 : null,
    });
  }
};

/** Every field list of a record: its own, and each lines field's, with the chain down to it. */
const fieldLists = (fields: readonly SetupDraftField[], within: string[] = []): Array<{ fields: readonly SetupDraftField[]; within: string[] }> => [
  { fields, within },
  ...fields.flatMap((field) => (field.type === 'lines' && field.lines !== undefined ? fieldLists(field.lines.fields, [...within, field.key]) : [])),
];

/**
 * A quantity with no rate beside it on an entry record, or on its lines. Only
 * an entry: a list's quantity (a reorder level, the stock in hand) is a fact
 * about the row, never an amount bought or sold at a rate, and a list holds no
 * typed money (`DIRECTORY_HOLDS_MONEY`; it only works money out from entries,
 * W11 of docs/plans/workbook-map.md), so a Rate could never be saved there.
 */
/** A typed money field that is the entry's whole amount, never a part of it like a discount. */
const AMOUNT_WORD = /\b(?:amount|total|cost|value|bill|jumlah|rakam|kharcha)\b/iu;

const qtyWithoutRate: Rule = (draft, _facts, raise) => {
  for (const record of draft.records) {
    if (!isEntries(record)) continue;
    for (const { fields, within } of fieldLists(record.fields)) {
      const qty = fields.find((field) => field.type === 'quantity');
      if (qty === undefined) continue;
      // An amount typed beside it (an expense's Amount, a day's Total) is the money: a rate would only ask twice
      // (restaurant, trucks, dairy, 30 Oct 2026).
      const priced = fields.some((field) => isMoney(field) && (RATE.test(field.label) || (field.type === 'money' && AMOUNT_WORD.test(field.label))));
      const worked = allFields(record.fields).some(({ field }) => field.type === 'formula' && formulaReads(field.formula, qty.key));
      if (priced || worked) continue;
      const taken = fieldKeysOf(fields);
      const rateKey = mintStudioKey('Rate', taken, 'f');
      taken.add(rateKey);
      const totalKey = mintStudioKey('Total', taken, 'f');
      const at = within.length === 0 ? {} : { within };
      const product: SetupDraftFormulaNode = { kind: 'calc', op: 'multiply', left: { kind: 'field', fieldKey: qty.key }, right: { kind: 'field', fieldKey: rateKey } };
      const ops: StudioOp[] = [
        { op: 'add_field', record: record.key, ...at, field: { key: rateKey, label: 'Rate', type: 'money' } },
        { op: 'add_field', record: record.key, ...at, field: { key: totalKey, label: 'Total', type: 'formula', formula: { result: 'money', expr: product, onMissing: 'blank' } } },
      ];
      const lines = within.at(-1);
      const fits = fieldsFit(fields, within, 2);
      raise({
        id: `qty_without_rate:${keyId(record.key, within)}`,
        rule: 'qty_without_rate',
        severity: 'should',
        on:
          lines === undefined
            ? { kind: 'record', record: record.key }
            : { kind: 'field', record: record.key, ...(within.length > 1 ? { within: within.slice(0, -1) } : {}), field: lines },
        questionKey: 'setup.studio.gap.rate_q',
        vars: { record: record.name },
        chips: [{ labelKey: 'setup.studio.gap.rate_fix', ops: fitting(fits, ops) }, NOT_NEEDED_CHIP],
        defaultChip: fits ? 0 : null,
      });
    }
  }
};

/** Whether any card groups by a top-level reference to `list`. */
const groupedBy = (draft: StudioDraft, list: string): boolean => {
  const refs = (recordKey: string, fieldKey: string | undefined): boolean => {
    const record = draft.records.find((one) => one.key === recordKey);
    const field = record?.fields.find((one) => one.key === fieldKey);
    return field !== undefined && refersTo(field, list);
  };
  if (draft.records.some((record) => (record.tiles ?? []).some((tile) => refs(record.key, tile.groupByFieldKey)))) return true;
  const leaves = (node: StudioDraft['boardCards'][number]['total']): Array<{ record: string; groupBy?: string }> =>
    node.kind === 'agg' ? [node] : node.kind === 'calc' ? [...leaves(node.left), ...leaves(node.right)] : node.kind === 'round' ? leaves(node.of) : [];
  return draft.boardCards.some((card) => leaves(card.total).some((leaf) => refs(leaf.record, leaf.groupBy)));
};

const stockWithoutMovement: Rule = (draft, _facts, raise) => {
  for (const list of draft.records) {
    if (!isList(list) || !(STOCK.test(list.name) || list.fields.some((field) => STOCK.test(field.label)))) continue;
    const topRef = (record: StudioRecord): SetupDraftField | undefined => record.fields.find((field) => field.type === 'reference' && field.targetRecordKey === list.key);
    const sale = draft.records.find((record) => record.kind === 'record' && record.direction === 'in' && topRef(record) !== undefined);
    // A pick inside an entry's lines cannot group a card, so only a top-level pick raises this.
    if (sale === undefined || groupedBy(draft, list.key)) continue;
    const receiver = draft.records.find((record) => record.kind === 'record' && (record.direction === 'out' || record.direction === 'count') && topRef(record) !== undefined) ?? sale;
    const ref = topRef(receiver)!;
    const qty = receiver.fields.find((field) => field.type === 'quantity');
    const fit = 'Stock in and sold, this month';
    const ops: StudioOp[] = [
      {
        op: 'add_card',
        record: receiver.key,
        card: {
          label: fit,
          card: 'insight.breakdown',
          agg: qty === undefined ? 'count' : 'sum',
          ...(qty === undefined ? {} : { fieldKey: qty.key }),
          groupByFieldKey: ref.key,
          scope: 'month',
        },
      },
      // Stock is never "not possible" (R4 of docs/plans/qa-fixes-2.md): a sale of one item is a link RecordOS builds, so
      // the note is kept only where a sale is a bill of lines, which no link reads yet (Q1).
      ...(sale.fields.some((field) => field.type === 'lines')
        ? [
            {
              op: 'not_yet' as const,
              item: { kind: 'other' as const, name: 'Stock falling as a bill is saved', fit, level: 'lifecycle' as const, archetype: archetypeOf(draft), need: 'stock falling as a bill is saved' },
            },
          ]
        : []),
    ];
    const notes = ops.some((op) => op.op === 'not_yet');
    const fits = tileFits(receiver) && (!notes || notYetFits(draft));
    raise({
      id: `stock_without_movement:${list.key}`,
      rule: 'stock_without_movement',
      severity: 'should',
      on: { kind: 'record', record: list.key },
      questionKey: 'setup.studio.gap.stock_q',
      vars: { list: list.name },
      chips: [{ labelKey: 'setup.studio.gap.stock_fix', ops: fitting(fits, ops) }, NOT_NEEDED_CHIP],
      defaultChip: fits ? 0 : null,
    });
  }
};

const watchUnanswered: Rule = (draft, _facts, raise) => {
  for (const note of draft.notes) {
    if (note.kind !== 'watch' || note.card !== undefined) continue;
    let ops: StudioOp[] = [];
    const owes = OWES.test(note.text) ? draft.records.find((record) => record.kind === 'record' && record.direction === 'in' && partyRefOf(draft, record) !== null) : undefined;
    const measure = owes === undefined ? undefined : (owes.measureFieldKey ?? owes.fields.find(isMoney)?.key);
    // A card adds up money: with none on the record (BOARD_NO_MEASURE), or no room on the Dashboard, the answer is OS's.
    if (owes !== undefined && measure !== undefined && draft.boardCards.length < STUDIO_LIMITS.boardCards) {
      const party = partyRefOf(draft, owes)!;
      const label = 'What each customer owes';
      const key = mintStudioKey(label, new Set(draft.boardCards.map((card) => card.key)), 'b');
      ops = [
        {
          op: 'add_board_card',
          card: {
            key,
            label,
            card: 'insight.breakdown',
            scope: 'all',
            total: {
              kind: 'agg',
              agg: 'sum',
              record: owes.key,
              of: owes.measureFieldKey !== undefined ? { kind: 'measure' } : { kind: 'field', field: measure },
              groupBy: party.key,
            },
          },
        },
        { op: 'update_note', note: note.key, card: key },
      ];
    }
    raise({
      id: `watch_unanswered:${note.key}`,
      rule: 'watch_unanswered',
      severity: 'should',
      on: WORKSPACE,
      questionKey: 'setup.studio.gap.watch_q',
      vars: { what: note.text },
      chips: [{ labelKey: 'setup.studio.gap.watch_fix', ops }, NOT_NEEDED_CHIP],
      defaultChip: ops.length === 0 ? null : 0,
    });
  }
};

/** The words of a name of four letters or more, lower-cased. */
const longWords = (text: string): Set<string> => new Set(text.toLocaleLowerCase().split(/[^\p{L}\p{N}]+/u).filter((word) => word.length >= 4));

const roleReach: Rule = (draft, _facts, raise) => {
  for (const role of draft.roles) {
    const roleWords = longWords(role.name);
    for (const access of role.access) {
      if (access.level === 'none') continue;
      const record = draft.records.find((one) => one.key === access.record);
      if (record === undefined) continue;
      const filter = allFields(record.fields)
        .map((one) => one.field)
        .find((field) => (field.type === 'boolean' || field.type === 'select') && [...longWords(field.label)].some((word) => roleWords.has(word)));
      if (filter === undefined) continue;
      const id = `role_reach:${role.key}.${record.key}`;
      raise({
        id,
        rule: 'role_reach',
        severity: 'should',
        on: { kind: 'role', key: role.key },
        titleKey: 'setup.studio.gap.role_title',
        questionKey: 'setup.studio.gap.role_q',
        vars: { role: role.name, record: record.name, filter: filter.label },
        chips: [
          {
            labelKey: 'setup.studio.gap.role_fix',
            vars: { filter: filter.label },
            ops: fitting(notYetFits(draft), [
              {
                op: 'not_yet',
                item: {
                  kind: 'role',
                  name: `Only ${filter.label} for ${role.name}`.slice(0, 80),
                  fit: `${role.name} sees all of ${record.name}`.slice(0, 200),
                  level: 'arrangement',
                  archetype: archetypeOf(draft),
                  need: 'a role that sees only some entries of a record',
                },
              },
              { op: 'dismiss_gap', gap: id },
            ]),
          },
          { labelKey: 'setup.studio.gap.role_all', ops: [], dismiss: true },
        ],
        defaultChip: notYetFits(draft) ? 0 : null,
      });
    }
  }
};

const sourceConflict: Rule = (draft, facts, raise) => {
  const later = new Set(draft.laterConflicts ?? []);
  for (const conflict of facts.conflicts) {
    raise({
      ...(conflict.sides === undefined ? {} : { conflict: { id: conflict.id, about: conflict.about ?? '', sides: conflict.sides } }),
      ...(later.has(conflict.id) ? { later: true as const } : {}),
      id: `source_conflict:${conflict.id}`,
      rule: 'source_conflict',
      severity: 'should',
      on: conflict.on,
      question: conflict.question,
      vars: {},
      chips: [
        { labelKey: 'setup.studio.gap.conflict_use', vars: { name: conflict.a }, ops: conflict.opsA },
        { labelKey: 'setup.studio.gap.conflict_use', vars: { name: conflict.b }, ops: conflict.opsB },
        NOT_NEEDED_CHIP,
      ],
      defaultChip: null,
    });
  }
};

const sourceUnplaced: Rule = (_draft, facts, raise) => {
  for (const source of facts.sources) {
    if ((source.state !== 'read' && source.state !== 'partly') || source.placed) continue;
    raise({
      id: `source_unplaced:${source.id}`,
      rule: 'source_unplaced',
      severity: 'should',
      on: WORKSPACE,
      questionKey: 'setup.studio.gap.unplaced_q',
      vars: { name: source.name },
      chips: [
        { labelKey: 'setup.studio.gap.place_it', ops: [] },
        { labelKey: 'setup.studio.gap.leave_out', ops: [], dismiss: true },
      ],
      defaultChip: null,
    });
  }
};

/** Rows a file holds that setup could not place on its own: the person says where they go, or leaves them out. */
const rowsUnsure: Rule = (draft, facts, raise) => {
  for (const sheet of facts.rows ?? []) {
    // A chip picked, or the plan made since: an import holds the sheet, and it is asked no more.
    if (draft.imports.some((one) => one.source === sheet.source && one.sheet === sheet.sheet)) continue;
    const options = sheet.options.filter((one) => draft.records.some((record) => record.key === one.record));
    if (options.length === 0) continue;
    // Leave out is the only way to skip, and says how many rows it leaves out.
    const leave: GapChip =
      sheet.rows === undefined
        ? { labelKey: 'setup.studio.gap.rows_leave', ops: [], dismiss: true }
        : { labelKey: 'setup.studio.gap.rows_leave_count', vars: { count: String(sheet.rows) }, ops: [], dismiss: true };
    const kinds = sheet.kinds ?? 0;
    raise({
      id: `rows_unsure:${sheet.id}`,
      rule: 'rows_unsure',
      severity: 'should',
      on: { kind: 'record', record: options[0]!.record },
      // A sheet whose rows mix kinds is asked once, with its kinds: each into its own record (W8).
      // A file of more than one sheet names the sheet too, so its sheets are told apart.
      ...((sheet.sheets ?? 1) > 1
        ? kinds > 1
          ? { questionKey: 'setup.studio.gap.rows_kinds_sheet_q' as const, vars: { name: sheet.name, sheet: sheet.sheet, count: String(kinds) } }
          : { questionKey: 'setup.studio.gap.rows_sheet_q' as const, vars: { name: sheet.name, sheet: sheet.sheet } }
        : kinds > 1
          ? { questionKey: 'setup.studio.gap.rows_kinds_q' as const, vars: { name: sheet.name, count: String(kinds) } }
          : { questionKey: 'setup.studio.gap.rows_q' as const, vars: { name: sheet.name } }),
      chips: [...options.map((one): GapChip => ({ labelKey: 'setup.studio.gap.rows_into', vars: { record: one.name }, ops: one.ops })), leave],
      defaultChip: null,
    });
  }
};

const orphanList: Rule = (draft, _facts, raise) => {
  for (const list of draft.records) {
    if (!isList(list) || (list.seedRows?.length ?? 0) > 0) continue;
    const picked = draft.records.some((record) => allFields(record.fields).some(({ field }) => refersTo(field, list.key)));
    const imported = draft.imports.some((plan) => plan.record === list.key);
    if (picked || imported) continue;
    raise({
      id: `orphan_list:${list.key}`,
      rule: 'orphan_list',
      severity: 'could',
      on: { kind: 'record', record: list.key },
      questionKey: 'setup.studio.gap.orphan_q',
      vars: { list: list.name },
      chips: [{ labelKey: 'setup.studio.gap.remove_it', ops: [{ op: 'remove_record', record: list.key, cascade: 'refuse' }] }, NOT_NEEDED_CHIP],
      defaultChip: 0,
    });
  }
};

const sameName = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Names joined for a sentence: "Rate", "Rate and Customer", "Rate, Customer and Notes". */

/** What a record's job is read by, leaving out when, notes and which way: its other fields. */
const jobFieldsOf = (record: StudioRecord, inOut: string): SetupDraftField[] =>
  record.fields.filter((field) => field.key !== inOut && field.type !== 'date' && field.type !== 'long_text' && field.type !== 'lines');

/** A record's own in/out choice: its sign field, else a choice whose options go both ways. */
const ownInOutOf = (record: StudioRecord): SetupDraftField | undefined =>
  record.fields.find((field) => field.key === record.signFieldKey && field.type === 'select') ??
  record.fields.find((field) => field.type === 'select' && [1, -1].every((sign) => (field.options ?? []).some((option) => option.sign === sign)));

/** Of a job's fields, how many the other record holds too, by key or name and type. */
const sharedJobFields = (fields: readonly SetupDraftField[], other: readonly SetupDraftField[]): number =>
  fields.filter((field) => other.some((one) => one.type === field.type && (one.key === field.key || sameName(one.label, field.label)))).length;

/** Two records hold one job by their fields when the smaller's job fields, three at least, are two thirds held by the other. */
const SAME_JOB_SHARE = 2 / 3;
const SAME_JOB_FIELDS_MIN = 3;

/** Words any record's name may carry beside what it keeps: "Udhaar Entries" and "Udhaar Khata" are Udhaar. */
const NAME_FILLERS = new Set(['entry', 'entries', 'log', 'logs', 'record', 'records', 'register', 'registers', 'list', 'lists', 'ledger', 'ledgers', 'sheet', 'sheets', 'details', 'khata']);

/** What a record's name says it keeps: its words, one of each, singular, without the fillers. Empty when it is fillers only. */
const nameStem = (name: string): string =>
  [...new Set(name.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((word) => word !== '' && !NAME_FILLERS.has(word)).map((word) => (word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word)))]
    .sort()
    .join(' ');

/** Whether two names keep one thing: the same words once the fillers are left out. "Cash Register" and "Petty Cash Register" are two. */
const sameStem = (a: string, b: string): boolean => {
  const stem = nameStem(a);
  return stem !== '' && stem === nameStem(b);
};

/**
 * Two records doing one job (D10 of docs/plans/qa-fixes-dairy.md). Sharma
 * Dairy, 18 Oct: the owner asked for milk in and out on Milk Collection, a new
 * Milk Movement was drawn to keep it, and Milk Collection then gained its own
 * Entry Type (In, Out) too; its link kept marking every entry In.
 *
 *   by fields    two records, each going in and out by its own choice over
 *                the same kind of measure, whose fields are mostly the same,
 *                and one job besides: the same name once "entries", "log"
 *                and the like are left out. The same fields are one shape,
 *                not one job: a Cash Register and a Bank Account keep two
 *                accounts (review of the fix diff, 18 Oct).
 *
 * The chips: remove B (with nothing else of the draft taken with it), or
 * keep both. Never decided for them. (Before the Links were retired, step 9.5
 * of docs/plans/ledger.md, a link feeding both records the same way was a
 * sign too.)
 */
const sameJob: Rule = (draft, _facts, raise) => {
  const said = new Set<string>();
  const ask = (from: StudioRecord, to: StudioRecord, questionKey: AppKey, vars: Record<string, string>): void => {
    if (said.has(to.key)) return;
    said.add(to.key);
    const ops: StudioOp[] = [{ op: 'remove_record', record: to.key, cascade: 'refuse' }];
    raise({
      id: `same_job:${to.key}`,
      rule: 'same_job',
      severity: 'should',
      on: { kind: 'record', record: to.key },
      titleKey: 'setup.studio.gap.same_job_title',
      questionKey,
      vars: { from: from.name, to: to.name, ...vars },
      chips: [
        { labelKey: 'setup.studio.gap.same_job_remove', vars: { record: to.name }, ops: fitting(commits(draft, ops), ops) },
        { labelKey: 'setup.studio.gap.same_job_keep', ops: [], dismiss: true },
      ],
      defaultChip: null,
    });
  };

  const twoWay = draft.records.filter(isEntries).flatMap((record) => {
    const inOut = ownInOutOf(record);
    const measure = record.fields.find((one) => one.key === record.measureFieldKey);
    return inOut === undefined ? [] : [{ record, inOut, money: measure !== undefined && isMoney(measure), job: jobFieldsOf(record, inOut.key) }];
  });
  twoWay.forEach((a, at) => {
    for (const b of twoWay.slice(at + 1)) {
      if (a.money !== b.money) continue;
      const [small, large] = a.job.length <= b.job.length ? [a, b] : [b, a];
      if (small.job.length < SAME_JOB_FIELDS_MIN || sharedJobFields(small.job, large.job) < small.job.length * SAME_JOB_SHARE) continue;
      // The later one is the one asked about: it is the one drawn second.
      if (sameStem(a.record.name, b.record.name)) ask(a.record, b.record, 'setup.studio.gap.same_job_fields_q', {});
    }
  });
};

const recordWithoutCard: Rule = (draft, _facts, raise) => {
  const onBoard = new Set(
    draft.boardCards.flatMap((card) => {
      const leaves = (node: typeof card.total): string[] =>
        node.kind === 'agg' ? [node.record] : node.kind === 'calc' ? [...leaves(node.left), ...leaves(node.right)] : node.kind === 'round' ? leaves(node.of) : [];
      return leaves(card.total);
    }),
  );
  for (const record of draft.records) {
    if (record.kind !== 'record' || (record.tiles ?? []).length > 0 || onBoard.has(record.key)) continue;
    const measure = record.fields.find((field) => field.key === record.measureFieldKey);
    // A record whose money goes nowhere has no measure to total (TILE_FIELD_MISSING): it is counted.
    const money = record.direction !== 'none' && measure !== undefined && isMoney(measure);
    const what = money ? 'setup.studio.gap.what_total' : 'setup.studio.gap.what_count';
    raise({
      id: `record_without_card:${record.key}`,
      rule: 'record_without_card',
      severity: 'could',
      on: { kind: 'record', record: record.key },
      questionKey: 'setup.studio.gap.card_q',
      vars: { record: record.name, what },
      chips: [
        {
          labelKey: 'setup.studio.gap.card_fix',
          vars: { what },
          ops: [{ op: 'add_card', record: record.key, card: { label: `${record.name} today`.slice(0, 80), card: 'insight.stat', agg: money ? 'sum' : 'count', scope: 'day' } }],
        },
        NOT_NEEDED_CHIP,
      ],
      defaultChip: 0,
    });
  }
};

/** Words a check's title is made of that say nothing of what it is about. */
const CHECK_FILLER = new Set(
  'a an and or the of on in to for with by from at as is are be do you your their our its it this that should would could can want need each per every all any some when what how into than also only just more about track tracking add adding attach list lists option options support record records entry entries field fields keep set use new own'.split(' '),
);

/** A word as a check and a part both say it: lower case, a plural made one. */
const checkWord = (word: string): string => (word.length > 4 && word.endsWith('ies') ? `${word.slice(0, -3)}y` : word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word);

const checkWordsOf = (...texts: readonly (string | undefined)[]): Set<string> =>
  new Set(
    texts
      .join(' ')
      .toLowerCase()
      .split(/[^\p{L}\p{N}]+/u)
      .filter((word) => word.length > 1)
      .map(checkWord),
  );

/** One word found among a part's words: the same, or one the other starts with, five letters or more. */
const sayWord = (word: string, words: ReadonlySet<string>): boolean =>
  words.has(word) || [...words].some((one) => Math.min(one.length, word.length) >= 5 && (one.startsWith(word) || word.startsWith(one)));

const fieldWordsOf = (field: SetupDraftField): string[] => [field.key.replace(/_/g, ' '), field.label, ...(field.options ?? []).map((option) => option.label)];

/**
 * Whether the draft already holds what a critic's check asks for: every word
 * of its title that names a thing is a word of one record (on the check's
 * part, when it is on one) and at most one of its fields, their options
 * included. "Payment mode for daily sales" is held by Daily Sales' Payment
 * mode; "Card payment option" is not, while no field offers Card.
 */
export const criticCheckHeld = (draft: StudioDraft, check: CriticCheck): boolean => {
  const asked = [...checkWordsOf(check.title)].filter((word) => !CHECK_FILLER.has(word));
  if (asked.length === 0) return false;
  const onRecord = check.on !== null && (check.on.kind === 'record' || check.on.kind === 'field' || check.on.kind === 'card') ? check.on.record : null;
  return draft.records.some((record) => {
    if (onRecord !== null && record.key !== onRecord) return false;
    const own = [record.name, record.key.replace(/_/g, ' '), record.entryNoun];
    const sets = [checkWordsOf(...own), ...allFields(record.fields).map(({ field }) => checkWordsOf(...own, ...fieldWordsOf(field)))];
    return sets.some((words) => asked.every((word) => sayWord(word, words)));
  });
};

/** The critic's checks (3.8), as `should` gaps whose chips are answers; one whose part is gone, or that the draft already holds, is dropped. */
const criticChecks: Rule = (draft, _facts, raise) => {
  for (const check of draft.checks ?? []) {
    if (check.on !== null && !studioPartExists(draft, studioPath(check.on))) continue;
    if (criticCheckHeld(draft, check)) continue;
    raise({
      id: `critic:${check.id}`,
      rule: 'critic',
      severity: 'should',
      on: check.on,
      title: check.title,
      question: check.question,
      why: check.why,
      vars: {},
      chips: check.chips.map(criticChip),
      defaultChip: null,
    });
  }
};

/**
 * Each difference from a built workspace that Edit cannot build yet (9.16), as
 * a `should` gap on its part with the reason's sentence: Build again builds
 * everything else and leaves these as they are.
 */
const editNotYet: Rule = (draft, facts, raise) => {
  if (facts.edit === undefined) return;
  for (const item of editChangesOf(facts.edit, draft).notYet) {
    raise({
      id: `edit_not_yet:${item.key}`,
      rule: 'edit_not_yet',
      severity: 'should',
      on: studioRefOf(item.key),
      questionKey: editNotYetKey(item.why),
      vars: { field: item.name, name: item.name },
      chips: [],
      defaultChip: null,
    });
  }
};

const GST_RATE = /\b(gst|tax|igst|cgst|sgst|vat)\b.*(\brate\b|%|\bpercent)|\brate\s+of\s+(gst|tax|vat)\b/i;
/** A field called only GST or Tax: its rate when it is not money (a money one is the tax's amount). */
const GST_WORD = /^\s*(gst|tax|igst|cgst|sgst|vat)\s*$/i;
const isGstRateField = (field: SetupDraftField): boolean => GST_RATE.test(field.label) || (GST_WORD.test(field.label) && !isMoney(field));
const GSTIN_FIELD = /\bgstin\b|\bgst\s*(no|number)\b/i;
const STATE_FIELD = /\bstate\b/i;
const HSN_FIELD = /\bhsn\b|\bsac\b/i;

/**
 * Document readiness (step 2.4 of docs/plans/report-templates.md): with a
 * GSTIN heard in setup and a record whose entries hold lines and money, what
 * a tax invoice from it will need: a GST rate on the lines, GSTIN and State
 * on the party list it picks, HSN on the items its lines pick. One `could`
 * gap on that record (the selling one first), its fix the fields it lacks.
 */
/** The chip's ops and the op that settles its gap as answered, so the question is asked once (2.4). */
const answering = (id: string, ops: readonly StudioOp[] | null): StudioOp[] =>
  ops === null ? [] : [...ops, { op: 'dismiss_gap', gap: id, reason: GAP_SETTLED.answered }];

/**
 * Each money record asks what its money is (2.4 of docs/plans/ledger.md):
 * "Is Daily sales money in, money out, money owed to you, or money you
 * owe?" Each answer's chip gives the record the rule that answer picks
 * (`moneyPlanOf`), with the Accounts rows it names, and settles the
 * question; It moves no money takes the rule off (L2). Asked only once the
 * money kit's Accounts list is in the draft, since a rule names its accounts
 * there, and never about the kit's own four forms.
 */
const moneyKind: Rule = (draft, _facts, raise) => {
  if (accountsListOf(draft) === null) return;
  for (const record of draft.records) {
    const held = postingOf(record);
    // Money moved between two places of the business's own (L36): a file said so, and no answer of the four fits it.
    // A record that already counts its money is not asked again: the question read as noise on every record that
    // said money in or out (ten setups, 30 Oct 2026). Only a money record with no rule yet is asked what its money is.
    if (!isMoneyRecord(record) || isMoneyKitForm(record) || held !== null) continue;
    const id = `money_kind:${record.key}`;
    const plan = (kind: MoneyKind): StudioOp[] => {
      const made = moneyPlanOf(draft, record, kind);
      const ops = answering(id, made === null ? null : moneyPlanOps(draft, record.key, made));
      return ops.length > 0 && commits(draft, ops) ? ops : [];
    };
    const none: GapChip = {
      labelKey: 'setup.studio.gap.money_kind_none',
      ops: [{ op: 'dismiss_gap', gap: id, reason: MONEY_NONE_REASON }],
    };
    if (record.direction === 'both') {
      raise({
        id,
        rule: 'money_kind',
        severity: 'could',
        on: { kind: 'record', record: record.key },
        questionKey: 'setup.studio.gap.money_kind_q',
        vars: { record: record.name },
        chips: [{ labelKey: 'setup.studio.gap.money_kind_both', ops: plan('in') }, none, NOT_NEEDED_CHIP],
        defaultChip: 0,
      });
      continue;
    }
    const chips = MONEY_KINDS.map((kind): GapChip => ({ labelKey: `setup.studio.gap.money_kind_${kind}`, ops: plan(kind) }));
    const guess = kindGuess(record, draft);
    raise({
      id,
      rule: 'money_kind',
      severity: 'could',
      on: { kind: 'record', record: record.key },
      questionKey: 'setup.studio.gap.money_kind_q',
      vars: { record: record.name },
      chips: [...chips, none, NOT_NEEDED_CHIP],
      defaultChip: chips[MONEY_KINDS.indexOf(guess)]!.ops.length > 0 ? MONEY_KINDS.indexOf(guess) : null,
    });
  }
};

/** The row of the Accounts list money taken home goes to: by its system key, else by its name. */
const forHomeRowAt = (list: StudioRecord): number =>
  (list.seedRows ?? []).findIndex((row) => row.account?.systemKey === 'drawings' || /^(for home|owner'?s? drawings?|drawings?)$/i.test(accountRowName(list, row) ?? ''));

/**
 * Asked once, when the draft holds the money kit's Money out (2.4): "Do you
 * take money from the business for home?" (L65: never "the shop" for a gym) A yes puts For home at the top of the
 * Accounts list, so it is the first choice of what a Money out was for, and
 * money taken home is never counted as a cost (L52).
 */
const forHome: Rule = (draft, _facts, raise) => {
  const list = accountsListOf(draft);
  const out = draft.records.find((record) => record.kind === 'record' && /^money out$/i.test(record.name.trim()));
  if (list === null || out === undefined) return;
  const id = 'for_home:workspace';
  const rows = [...(list.seedRows ?? [])];
  const at = forHomeRowAt(list);
  const home = at >= 0 ? rows.splice(at, 1)[0]! : accountSeedRow(list, { name: 'For home', type: 'equity' }, 'drawings');
  const yes = answering(id, [{ op: 'seed_list', record: list.key, rows: [home, ...rows], mode: 'replace' }]);
  raise({
    id,
    rule: 'for_home',
    severity: 'should',
    on: { kind: 'record', record: out.key },
    questionKey: 'setup.studio.gap.for_home_q',
    vars: {},
    chips: [
      { labelKey: 'setup.studio.gap.for_home_yes', ops: commits(draft, yes) ? yes : [] },
      { labelKey: 'setup.studio.gap.for_home_no', ops: [], dismiss: true },
    ],
    defaultChip: null,
  });
};

/**
 * A row of the Accounts list a payment mode made whose name does not say
 * what it is (2.7, L34): "Is PNB OD money you have, like a bank account, or
 * money you owe, like an overdraft?" Each chip sets its type and settles it.
 */
const accountType: Rule = (draft, _facts, raise) => {
  const list = accountsListOf(draft);
  const typeKey = list?.accounts?.typeFieldKey;
  if (list === null || typeKey === undefined) return;
  // The rows a rule names as where money is: the places of each record's rule, by name.
  const places = new Set<string>();
  const walk = (source: unknown): void => {
    if (typeof source !== 'object' || source === null) return;
    const held = source as { row?: unknown; options?: Record<string, unknown> };
    if (typeof held.row === 'string') places.add(held.row.trim().toLowerCase());
    for (const one of Object.values(held.options ?? {})) walk(one);
  };
  for (const record of draft.records) {
    const rule = postingOf(record);
    const roleSets = [rule?.roles ?? {}, ...Object.values(rule?.byChoice?.options ?? {}).map((one) => one.roles ?? {})];
    for (const roles of roleSets) for (const role of ['paid_to', 'paid_from', 'from', 'to']) walk(roles[role]);
  }
  (list.seedRows ?? []).forEach((row, index) => {
    const name = accountRowName(list, row);
    if (name === null || row.account?.systemKey !== undefined || !places.has(name.toLowerCase()) || modeAccountOf(name, 'in').row?.type !== null) return;
    const id = `account_type:${list.key}.${mintStudioKey(name, new Set(), 'x')}`;
    const typed = (type: 'asset' | 'liability'): StudioOp[] => {
      const rows = (list.seedRows ?? []).map((one, at) => (at !== index ? one : { ...one, values: [...one.values.filter((value) => value.fieldKey !== typeKey), { fieldKey: typeKey, value: type }] }));
      const ops = answering(id, [{ op: 'seed_list', record: list.key, rows, mode: 'replace' }]);
      return commits(draft, ops) ? ops : [];
    };
    raise({
      id,
      rule: 'account_type',
      severity: 'should',
      on: { kind: 'record', record: list.key },
      questionKey: 'setup.studio.gap.account_type_q',
      vars: { account: name },
      chips: [
        { labelKey: 'setup.studio.gap.account_have', ops: typed('asset') },
        { labelKey: 'setup.studio.gap.account_owe', ops: typed('liability') },
      ],
      defaultChip: null,
    });
  });
};

const documentsReady: Rule = (draft, facts, raise) => {
  if (!(facts.detailKeys ?? []).includes('gstin')) return;
  const withLines = draft.records.filter(
    (record) => isEntries(record) && record.fields.some((field) => field.type === 'lines' && field.lines !== undefined) && allFields(record.fields).some(({ field }) => isMoney(field)),
  );
  const record = withLines.find((one) => one.direction === 'in') ?? withLines[0];
  if (record === undefined) return;
  const lines = record.fields.find((field) => field.type === 'lines' && field.lines !== undefined)!;
  const lineFields = lines.lines!.fields;
  const listOf = (field: SetupDraftField | undefined): StudioRecord | undefined =>
    field === undefined ? undefined : draft.records.find((one) => isList(one) && one.key === field.targetRecordKey);
  const party = listOf(record.fields.find((field) => field.type === 'reference' && listOf(field) !== undefined && PEOPLE.test(listOf(field)!.name)) ?? record.fields.find((field) => field.type === 'reference' && listOf(field) !== undefined));
  const items = listOf(lineFields.find((field) => field.type === 'reference' && listOf(field) !== undefined && listOf(field)!.key !== party?.key));
  const labelled = (fields: readonly SetupDraftField[], pattern: RegExp): boolean => fields.some((field) => pattern.test(field.label));
  const ops: StudioOp[] = [];
  let fits = true;
  const add = (on: StudioRecord, within: string[], fields: readonly SetupDraftField[], labels: readonly string[]): void => {
    if (labels.length === 0) return;
    if (!fieldsFit(fields, within, labels.length)) fits = false;
    const taken = fieldKeysOf(fields);
    for (const label of labels) {
      const key = mintStudioKey(label, taken, 'f');
      taken.add(key);
      ops.push({ op: 'add_field', record: on.key, ...(within.length === 0 ? {} : { within }), field: { key, label, type: label === 'GST rate' ? 'number' : 'text' } });
    }
  };
  add(record, [lines.key], lineFields, lineFields.some(isGstRateField) ? [] : ['GST rate']);
  if (party !== undefined) add(party, [], party.fields, [...(labelled(party.fields, GSTIN_FIELD) ? [] : ['GSTIN']), ...(labelled(party.fields, STATE_FIELD) ? [] : ['State'])]);
  if (items !== undefined) add(items, [], items.fields, labelled(items.fields, HSN_FIELD) ? [] : ['HSN']);
  if (ops.length === 0) return;
  raise({
    id: `documents_ready:${record.key}`,
    rule: 'documents_ready',
    severity: 'could',
    on: { kind: 'record', record: record.key },
    questionKey: 'setup.studio.gap.documents_q',
    vars: { record: record.name },
    chips: [
      { labelKey: 'setup.studio.gap.documents_fix', ops: fitting(fits, ops) },
      { labelKey: 'setup.studio.gap.not_now', ops: [], dismiss: true },
    ],
    defaultChip: fits ? 0 : null,
  });
};

/**
 * A party list that already has a field named like one of the three money
 * figures Build gives it (Billed, Received, Pending: `addLedgerRollups`, L33).
 * Build does not rename the person's field: it names its own figure apart
 * ("Billed (accounts)"), and this says so before Build. The words are the
 * English ones Build writes; a workspace built in other words is still built.
 */
const FIGURE_LABELS: ReadonlySet<string> = new Set(['billed', 'received', 'pending']);

const figureLabelTaken: Rule = (draft, _facts, raise) => {
  for (const key of partyListsOf(draft)) {
    const list = draft.records.find((one) => one.key === key);
    if (list === undefined) continue;
    for (const field of list.fields) {
      if (!FIGURE_LABELS.has(field.label.trim().toLowerCase())) continue;
      raise({
        id: `figure_label_taken:${list.key}.${field.key}`,
        rule: 'figure_label_taken',
        severity: 'could',
        on: { kind: 'record', record: list.key },
        questionKey: 'setup.studio.gap.figure_label_taken_q',
        vars: { list: list.name, label: field.label },
        chips: [NOT_NEEDED_CHIP],
        defaultChip: null,
      });
    }
  }
};

/** The person's words for keeping the records alone: "only me", "main hi likhta hoon", "saya sendiri". */
const ALONE =
  /\b(?:only me|just me|only i|i alone|by myself|myself only|no staff|no one else|nobody else|main hi|mai hi|sirf main|sirf mai|akela|akele|khud hi|koi staff nahi|saya sendiri|sendiri saja|hanya saya|cuma saya|tiada pekerja)\b/u;

/**
 * Who else writes entries (S8 of the 30 Oct runs: six owners with staff
 * finished setup with no role, and none was asked). Asked once, as a could,
 * when the draft takes entries, holds no role, and the person has not said
 * they keep the records alone. Neither chip changes the draft: Only me leaves
 * the check, and the other is words for OS, who asks who and what they see.
 * An edit of a built workspace never asks it (`EDIT_RULES`).
 */
const whoRecords: Rule = (draft, facts, raise) => {
  if (draft.roles.length > 0 || !draft.records.some(isEntries) || ALONE.test(facts.said)) return;
  raise({
    id: 'who_records:workspace',
    rule: 'who_records',
    severity: 'could',
    on: WORKSPACE,
    questionKey: 'setup.studio.gap.who_records_q',
    vars: {},
    chips: [
      { labelKey: 'setup.studio.gap.who_records_others', ops: [] },
      { labelKey: 'setup.studio.gap.who_records_only_me', ops: [], dismiss: true },
    ],
    defaultChip: null,
  });
};

/** The walk's words for each check that blocks: its title, its question, and the one chip OS reads. */
const WALK_WORDS: Partial<Record<WalkCheckId, readonly [AppKey, AppKey, AppKey]>> = {
  amount_unreachable: ['setup.studio.gap.walk_amount_title', 'setup.studio.gap.walk_amount_q', 'setup.studio.gap.walk_amount_fix'],
  no_price: ['setup.studio.gap.walk_no_price_title', 'setup.studio.gap.walk_no_price_q', 'setup.studio.gap.walk_no_price_fix'],
  price_from_list: ['setup.studio.gap.walk_rows_title', 'setup.studio.gap.walk_rows_q', 'setup.studio.gap.walk_rows_fix'],
  dues_with_no_party: ['setup.studio.gap.walk_party_title', 'setup.studio.gap.walk_party_q', 'setup.studio.gap.walk_party_fix'],
  no_settle_path: ['setup.studio.gap.walk_settle_title', 'setup.studio.gap.walk_settle_q', 'setup.studio.gap.walk_settle_fix'],
};

/**
 * The setup walk's blocks (W8, W9 of docs/plans/setup-walk.md): what the
 * owner could not finish using the setup, after the money pass laid every
 * safe fix. Each is asked once, as a should: a walk can be wrong, so it
 * never stops a build. Its chip holds no ops: the words go to OS, who makes
 * the change in the next turn. A hint is never asked (W4).
 */
const setupWalk: Rule = (draft, _facts, raise) => {
  for (const found of walkSetup(draft)) {
    const words = WALK_WORDS[found.check];
    if (found.level !== 'blocks' || words === undefined) continue;
    const record = draft.records.find((one) => one.key === found.record);
    const list = draft.records.find((one) => one.key === found.list);
    const fieldOf = (holder: StudioRecord | undefined, key: string | undefined): string => holder?.fields.find((one) => one.key === key)?.label ?? '';
    raise({
      id: found.id,
      rule: 'setup_walk',
      severity: 'should',
      on: found.field === undefined || record === undefined ? { kind: 'record', record: found.record } : { kind: 'field', record: found.record, field: found.field },
      titleKey: words[0],
      questionKey: words[1],
      vars: {
        record: record?.name ?? found.record,
        field: found.check === 'price_from_list' ? fieldOf(list, found.listField) : fieldOf(record, found.field),
        list: list?.name ?? '',
        rows: (found.rows ?? []).slice(0, 5).join(', '),
      },
      why: found.says,
      chips: [{ labelKey: words[2], ops: [] }],
      defaultChip: null,
    });
  }
};

/** The rules an edit of a built workspace runs: a weeks-old workspace is not asked again for what it chose to be. */
const EDIT_RULES: ReadonlySet<Rule> = new Set([noName, danglingReference, criticChecks, editNotYet]);

const RULES: readonly Rule[] = [
  noName,
  nameTaken,
  noRecord,
  danglingReference,
  creditWithoutDues,
  saleWithoutPayment,
  qtyWithoutRate,
  stockWithoutMovement,
  watchUnanswered,
  roleReach,
  sourceConflict,
  sourceUnplaced,
  rowsUnsure,
  orphanList,
  recordWithoutCard,
  sameJob,
  criticChecks,
  editNotYet,
  documentsReady,
  moneyKind,
  forHome,
  accountType,
  figureLabelTaken,
  setupWalk,
  whoRecords,
];

/**
 * The open gaps of a draft: every rule, then the critic's checks; a gap the
 * person left (`dismissedGaps`) dropped unless it is a blocker, sorted blocker, should, could, then by draft order. Every `should`
 * and `could` gap ends with a chip that leaves it.
 */
export const gapsOf = (draft: StudioDraft, facts: GapFacts = NO_GAP_FACTS): SetupGap[] => {
  const raised: SetupGap[] = [];
  const raise: Raise = (gap) => {
    const ends = gap.chips.at(-1)?.dismiss === true;
    raised.push(gap.severity === 'blocker' || ends ? gap : { ...gap, chips: [...gap.chips, NOT_NEEDED_CHIP] });
  };
  for (const rule of RULES) if (facts.edit === undefined || EDIT_RULES.has(rule)) rule(draft, facts, raise);
  const left = new Set(draft.dismissedGaps.map((one) => one.gap));
  return raised
    // Only a gap with a chip that leaves it can be left: a blocker stays whatever `dismissedGaps` says.
    .filter((gap) => gap.severity === 'blocker' || !left.has(gap.id))
    .map((gap, index) => ({ gap, index, at: orderOf(draft, gap.on) }))
    .sort((a, b) => SEVERITY_ORDER[a.gap.severity] - SEVERITY_ORDER[b.gap.severity] || a.at - b.at || a.index - b.index)
    .map(({ gap }) => gap);
};

/**
 * A whole-draft problem as a blocker: one per problem a draft carries (a
 * restore, a draft opened under a newer manifest). Never stored. It has no
 * chips.
 */
export const draftProblemGaps = (problems: readonly StudioProblem[]): SetupGap[] =>
  problems.map((problem, index): SetupGap => ({
    id: `draft_problem:${problem.code}.${index}`,
    rule: 'draft_problem',
    severity: 'blocker',
    on: problem.part === null ? null : studioRefOf(problem.part),
    questionKey: 'setup.studio.fail.changed',
    vars: {},
    chips: [],
    defaultChip: null,
  }));
